/**
 * Reusable Skeleton Loading Components
 * Professional placeholder animations for loading states
 */

// Base skeleton pulse animation with glassmorphism
const pulseClass = "animate-pulse bg-gradient-to-r from-neutral-200/80 via-neutral-100/80 to-neutral-200/80 dark:from-white/10 dark:via-white/5 dark:to-white/10 bg-[length:200%_100%]";

/**
 * Simple skeleton box with customizable dimensions
 */
export function SkeletonBox({ className = "", rounded = "rounded" }) {
  return <div className={`${pulseClass} ${rounded} ${className}`} />;
}

/**
 * Skeleton for text lines
 */
export function SkeletonText({ lines = 1, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={`${pulseClass} h-4 rounded ${i === lines - 1 && lines > 1 ? "w-3/4" : "w-full"}`}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton for avatar/profile image
 */
export function SkeletonAvatar({ size = "md", className = "" }) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
    xl: "w-16 h-16",
  };
  return <div className={`${pulseClass} rounded-full ${sizeClasses[size]} ${className}`} />;
}

/**
 * Skeleton for Course Card
 */
export function SkeletonCourseCard() {
  return (
    <div className="glass rounded-2xl shadow-sm border border-white/20 dark:border-white/10 overflow-hidden">
      {/* Header gradient bar */}
      <div className={`h-3 ${pulseClass}`} />
      
      <div className="p-6">
        {/* Badges */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <SkeletonBox className="h-6 w-16 rounded-full" />
          <SkeletonBox className="h-6 w-20 rounded-full" />
        </div>

        {/* Icon */}
        <SkeletonBox className="w-12 h-12 rounded-xl mb-4" />

        {/* Title */}
        <SkeletonBox className="h-6 w-3/4 rounded mb-2" />
        
        {/* Description */}
        <SkeletonText lines={2} className="mb-5" />

        {/* Button */}
        <SkeletonBox className="h-10 w-full rounded-xl" />
      </div>
    </div>
  );
}

/**
 * Skeleton for Course Cards Grid
 */
export function SkeletonCourseGrid({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCourseCard key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for Job Card
 */
export function SkeletonJobCard() {
  return (
    <div className="glass rounded-xl border border-white/20 dark:border-white/10 p-5 shadow-sm">
      <div className="flex gap-4">
        {/* Company logo */}
        <SkeletonBox className="w-14 h-14 rounded-xl flex-shrink-0" />
        
        <div className="flex-1 min-w-0">
          {/* Title */}
          <SkeletonBox className="h-5 w-3/4 rounded mb-2" />
          
          {/* Company name */}
          <SkeletonBox className="h-4 w-1/2 rounded mb-3" />
          
          {/* Tags */}
          <div className="flex gap-2 flex-wrap">
            <SkeletonBox className="h-6 w-16 rounded-full" />
            <SkeletonBox className="h-6 w-20 rounded-full" />
            <SkeletonBox className="h-6 w-14 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for Job Cards List
 */
export function SkeletonJobList({ count = 5 }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonJobCard key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for Chapter Card
 */
export function SkeletonChapterCard() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex items-start gap-4">
        {/* Chapter number */}
        <SkeletonBox className="w-12 h-12 rounded-xl flex-shrink-0" />
        
        <div className="flex-1">
          {/* Title */}
          <SkeletonBox className="h-5 w-2/3 rounded mb-2" />
          
          {/* Description */}
          <SkeletonText lines={2} className="mb-3" />
          
          {/* Progress bar */}
          <SkeletonBox className="h-2 w-full rounded-full mb-2" />
          
          {/* Stats */}
          <div className="flex gap-4">
            <SkeletonBox className="h-4 w-20 rounded" />
            <SkeletonBox className="h-4 w-16 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for Topic Card
 */
export function SkeletonTopicCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
      {/* Title */}
      <SkeletonBox className="h-5 w-3/4 rounded mb-3" />
      
      {/* Badges */}
      <div className="flex gap-2 mb-3">
        <SkeletonBox className="h-6 w-16 rounded-full" />
        <SkeletonBox className="h-6 w-14 rounded-full" />
      </div>
      
      {/* Progress */}
      <div className="flex items-center justify-between">
        <SkeletonBox className="h-6 w-24 rounded-full" />
        <SkeletonBox className="h-4 w-12 rounded" />
      </div>
    </div>
  );
}

/**
 * Skeleton for Topic Cards Grid
 */
export function SkeletonTopicGrid({ count = 8 }) {
  return (
    <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonTopicCard key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for Leaderboard Entry
 */
export function SkeletonLeaderboardEntry() {
  return (
    <div className="flex items-center gap-3 py-3 px-2">
      {/* Rank */}
      <SkeletonBox className="w-6 h-5 rounded" />
      
      {/* Avatar */}
      <SkeletonAvatar size="md" />
      
      {/* Name */}
      <div className="flex-1">
        <SkeletonBox className="h-4 w-24 rounded" />
      </div>
      
      {/* Score */}
      <SkeletonBox className="h-5 w-12 rounded" />
    </div>
  );
}

/**
 * Skeleton for Leaderboard
 */
export function SkeletonLeaderboard({ count = 5 }) {
  return (
    <div className="space-y-1">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonLeaderboardEntry key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for Profile Card
 */
export function SkeletonProfileCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
      <div className="flex items-center gap-4 mb-6">
        <SkeletonAvatar size="xl" />
        <div className="flex-1">
          <SkeletonBox className="h-6 w-32 rounded mb-2" />
          <SkeletonBox className="h-4 w-48 rounded" />
        </div>
      </div>
      <SkeletonText lines={3} />
    </div>
  );
}

/**
 * Skeleton for Content Section (generic)
 */
export function SkeletonContentSection({ hasTitle = true }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
      {hasTitle && <SkeletonBox className="h-6 w-1/3 rounded mb-4" />}
      <SkeletonText lines={4} />
    </div>
  );
}

/**
 * Full page skeleton for course details
 */
export function SkeletonCourseDetails() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
        <SkeletonBox className="h-8 w-2/3 rounded mb-3" />
        <SkeletonText lines={2} className="mb-4" />
        <div className="flex gap-3">
          <SkeletonBox className="h-10 w-32 rounded-xl" />
          <SkeletonBox className="h-10 w-28 rounded-xl" />
        </div>
      </div>
      
      {/* Chapters */}
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonChapterCard key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * Full page skeleton for topic list
 */
export function SkeletonTopicList() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
        <SkeletonBox className="h-8 w-1/2 rounded mb-3" />
        <SkeletonText lines={2} className="mb-4" />
        <SkeletonBox className="h-2 w-full rounded-full" />
      </div>
      
      {/* Topics grid */}
      <SkeletonTopicGrid count={8} />
    </div>
  );
}

/**
 * Skeleton for User Dashboard
 */
export function SkeletonDashboard() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Hero Section Skeleton */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="ca-container py-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <SkeletonBox className="w-28 h-28 rounded-2xl" />
            <div className="flex-1 text-center md:text-left">
              <SkeletonBox className="h-8 w-48 rounded mb-3 mx-auto md:mx-0" />
              <SkeletonBox className="h-5 w-64 rounded mx-auto md:mx-0" />
            </div>
          </div>
        </div>
      </div>

      <div className="ca-container py-8 space-y-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
              <SkeletonBox className="h-4 w-24 rounded mb-3" />
              <SkeletonBox className="h-8 w-16 rounded mb-2" />
              <SkeletonBox className="h-3 w-20 rounded" />
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
            <SkeletonBox className="h-6 w-40 rounded mb-4" />
            <SkeletonBox className="h-48 w-full rounded" />
          </div>
          {/* Progress Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
            <SkeletonBox className="h-6 w-40 rounded mb-4" />
            <div className="flex items-center justify-center h-48">
              <SkeletonBox className="w-32 h-32 rounded-full" />
            </div>
          </div>
        </div>

        {/* Course Progress */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
          <SkeletonBox className="h-6 w-48 rounded mb-6" />
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <SkeletonBox className="h-10 w-10 rounded-lg" />
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-32 rounded mb-2" />
                  <SkeletonBox className="h-2 w-full rounded-full" />
                </div>
                <SkeletonBox className="h-5 w-12 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
          <SkeletonBox className="h-6 w-36 rounded mb-6" />
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="text-center">
                <SkeletonBox className="w-14 h-14 rounded-xl mx-auto mb-2" />
                <SkeletonBox className="h-3 w-16 rounded mx-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for Admin Analytics Tab
 */
export function SkeletonAnalytics() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <SkeletonBox className="h-7 w-48 rounded mb-2" />
          <SkeletonBox className="h-4 w-64 rounded" />
        </div>
        <SkeletonBox className="h-10 w-32 rounded" />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div>
                <SkeletonBox className="h-4 w-20 rounded mb-2" />
                <SkeletonBox className="h-8 w-24 rounded" />
              </div>
              <SkeletonBox className="w-12 h-12 rounded-xl" />
            </div>
            <SkeletonBox className="h-3 w-32 rounded" />
          </div>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
            <SkeletonBox className="h-6 w-40 rounded mb-4" />
            <SkeletonBox className="h-56 w-full rounded" />
          </div>
        ))}
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
            <SkeletonBox className="h-6 w-32 rounded mb-4" />
            <div className="flex items-center justify-center h-40">
              <SkeletonBox className="w-28 h-28 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for Job Details Page
 */
export function SkeletonJobDetails() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="ca-container py-8">
          <div className="flex items-start gap-6">
            <SkeletonBox className="w-20 h-20 rounded-2xl flex-shrink-0" />
            <div className="flex-1">
              <SkeletonBox className="h-8 w-3/4 rounded mb-3" />
              <SkeletonBox className="h-5 w-48 rounded mb-4" />
              <div className="flex flex-wrap gap-2">
                <SkeletonBox className="h-7 w-20 rounded-full" />
                <SkeletonBox className="h-7 w-24 rounded-full" />
                <SkeletonBox className="h-7 w-28 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ca-container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
              <SkeletonBox className="h-6 w-40 rounded mb-4" />
              <SkeletonText lines={8} />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
              <SkeletonBox className="h-6 w-32 rounded mb-4" />
              <SkeletonText lines={5} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
              <SkeletonBox className="h-12 w-full rounded-xl mb-3" />
              <SkeletonBox className="h-10 w-full rounded-xl" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
              <SkeletonBox className="h-5 w-24 rounded mb-4" />
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <SkeletonBox className="w-5 h-5 rounded" />
                    <SkeletonBox className="h-4 w-32 rounded" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for Admin Data Table
 */
export function SkeletonAdminTable({ rows = 8, columns = 5 }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Table Header */}
      <div className="border-b border-gray-100 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <SkeletonBox className="h-5 w-32 rounded" />
          <div className="flex gap-3">
            <SkeletonBox className="h-10 w-48 rounded" />
            <SkeletonBox className="h-10 w-24 rounded" />
          </div>
        </div>
      </div>

      {/* Table Rows */}
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <div key={rowIdx} className="px-6 py-4 flex items-center gap-4">
            {Array.from({ length: columns }).map((_, colIdx) => (
              <SkeletonBox
                key={colIdx}
                className={`h-4 rounded ${
                  colIdx === 0 ? "w-8" : colIdx === columns - 1 ? "w-20" : "flex-1"
                }`}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="border-t border-gray-100 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <SkeletonBox className="h-4 w-40 rounded" />
          <div className="flex gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonBox key={i} className="h-8 w-8 rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for Form Pages (Add/Edit Course, Chapter, Topic)
 */
export function SkeletonFormPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="ca-container max-w-3xl py-8">
        {/* Back button */}
        <SkeletonBox className="h-5 w-32 rounded mb-6" />

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          {/* Header */}
          <div className={`${pulseClass} h-28`} />

          {/* Form Fields */}
          <div className="p-6 sm:p-8 space-y-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i}>
                <SkeletonBox className="h-4 w-32 rounded mb-3" />
                <SkeletonBox className={`h-12 w-full rounded ${i === 1 ? "h-24" : ""}`} />
              </div>
            ))}

            {/* Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-100 dark:border-gray-700">
              <SkeletonBox className="h-12 flex-1 rounded-xl" />
              <SkeletonBox className="h-12 flex-1 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for Company Grid
 */
export function SkeletonCompanyGrid({ count = 12 }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm">
          <SkeletonBox className="w-16 h-16 rounded-xl mx-auto mb-3" />
          <SkeletonBox className="h-4 w-20 rounded mx-auto mb-2" />
          <SkeletonBox className="h-3 w-12 rounded mx-auto" />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for Topic View (Theory/Video/MCQ/Coding)
 */
export function SkeletonTopicView() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="ca-container py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <SkeletonBox className="h-4 w-20 rounded" />
          <SkeletonBox className="h-4 w-4 rounded" />
          <SkeletonBox className="h-4 w-24 rounded" />
          <SkeletonBox className="h-4 w-4 rounded" />
          <SkeletonBox className="h-4 w-32 rounded" />
        </div>

        {/* Topic Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <SkeletonBox className="h-8 w-3/4 rounded mb-3" />
              <div className="flex gap-2">
                <SkeletonBox className="h-6 w-16 rounded-full" />
                <SkeletonBox className="h-6 w-20 rounded-full" />
              </div>
            </div>
            <SkeletonBox className="h-10 w-28 rounded-lg" />
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
          <SkeletonText lines={12} />
        </div>
      </div>
    </div>
  );
}
